// src/components/ColumnWithStore.tsx
import React, { useRef, useEffect, useState } from 'react';
import { GatheringStatus, DragItemTypes } from '../../../../../types';
import { useDrop } from 'react-dnd';
import GatheringCard from '../../gatherings/GatheringCard';
import { useGatheringsByStatus } from '../../../hooks/data/useGatheringByStatus';
import { usePendingEditSweep } from '../../../hooks/data/usePendingEditSweep';
import { FilterState } from '../../../hooks/useFilters';
import { Menu, MenuItem, IconButton, Tooltip } from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import EditNoteIcon from '@mui/icons-material/EditNote';

interface ColumnProps {
  status: GatheringStatus;
  token: string | null;
  onUpdateStatus: (gatheringId: string, newStatus: GatheringStatus) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onViewDetails?: (gatheringId: string) => void;
  filters: FilterState;
}

const Column: React.FC<ColumnProps> = ({ 
  status, 
  token,
  onUpdateStatus, 
  collapsed = false,
  onToggleCollapse,
  onViewDetails,
  filters
}) => {
  const columnRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Per-column sort state with localStorage persistence
  const [sortBy, setSortBy] = useState<'gathering_date' | 'submission_date'>(() => {
    const saved = localStorage.getItem(`column-sort-${status}`);
    return (saved === 'submission_date' ? 'submission_date' : 'gathering_date') as 'gathering_date' | 'submission_date';
  });
  
  // Menu state for sort dropdown
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);
  const sortMenuOpen = Boolean(sortMenuAnchor);
  
  // Save sort preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(`column-sort-${status}`, sortBy);
  }, [sortBy, status]);
  
  // Handle sort menu open
  const handleSortMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortMenuAnchor(event.currentTarget);
  };
  
  // Handle sort menu close
  const handleSortMenuClose = () => {
    setSortMenuAnchor(null);
  };
  
  // Handle sort option selection
  const handleSortSelect = (option: 'gathering_date' | 'submission_date') => {
    setSortBy(option);
    handleSortMenuClose();
  };

  // Use our custom hook to get gatherings for this status
  const { 
    gatherings: allGatherings, 
    isLoading, 
    isBackgroundLoading,
    error, 
    loadMore,
    refresh 
  } = useGatheringsByStatus(status, token);
  
  // Add drop functionality
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: DragItemTypes.GATHERING,
    drop: (item: { id: string, currentStatus: GatheringStatus }) => {
      if (item.currentStatus !== status) {
        onUpdateStatus(item.id, status);
      }
      return { status };
    },
    canDrop: (item: { id: string, currentStatus: GatheringStatus }) => {
      // Prevent dropping in the same column
      return item.currentStatus !== status;
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop()
    })
  });
  
  // Connect the drop to our ref
  drop(contentRef);
  
  // Apply filters to gatherings
  const gatherings = allGatherings.filter(gathering => {
    // Apply space filter
    if (filters.spaceId && gathering.space?.id !== filters.spaceId) {
      return false;
    }

    // Apply date range filter based on selected filter type
    if (filters.fromDate || filters.toDate) {
      let dateToCompare: Date;
      
      if (filters.dateFilterType === 'submission') {
        // Filter by submission date (created_at)
        if (!gathering.created_at) {
          return false; // Exclude gatherings without created_at if filtering by submission date
        }
        dateToCompare = new Date(gathering.created_at);
      } else {
        // Filter by gathering date (default)
        dateToCompare = new Date(gathering.date);
      }
      
      if (filters.fromDate && dateToCompare < filters.fromDate) {
        return false;
      }
      
      if (filters.toDate) {
        // Set time to end of day for toDate comparison
        const endOfDay = new Date(filters.toDate);
        endOfDay.setHours(23, 59, 59, 999);
        
        if (dateToCompare > endOfDay) {
          return false;
        }
      }
    }

    // Apply search filter
    if (filters.searchQuery && filters.searchQuery.trim() !== '') {
      const query = filters.searchQuery.toLowerCase().trim();
      
      // Search in gathering title
      const titleMatch = gathering.title?.toLowerCase().includes(query);
      
      // Search in owner names
      let ownerMatch = false;
      if (gathering.owner) {
        const firstName = gathering.owner.first_name?.toLowerCase() || '';
        const lastName = gathering.owner.last_name?.toLowerCase() || '';
        const displayName = gathering.owner.display_name?.toLowerCase() || '';
        const fullName = `${firstName} ${lastName}`.trim();
        
        ownerMatch = 
          firstName.includes(query) ||
          lastName.includes(query) ||
          fullName.includes(query) ||
          displayName.includes(query);
      }
      
      // Return true if either title or owner matches
      if (!titleMatch && !ownerMatch) {
        return false;
      }
    }

    // Gathering passed all filters
    return true;
  });

  // Sort gatherings based on selected sort preference
  const sortedGatherings = [...gatherings].sort((a, b) => {
    if (sortBy === 'submission_date') {
      // Sort by created_at (submission date)
      // Gatherings without created_at go to the end
      if (!a.created_at && !b.created_at) return 0;
      if (!a.created_at) return 1;
      if (!b.created_at) return -1;
      
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateA.getTime() - dateB.getTime();
    } else {
      // Sort by gathering date (default)
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    }
  });

  // ENG-1097: host content edits to a live gathering are STAGED — the gathering
  // stays Published, so it never lands in a review column and would otherwise
  // be invisible here. Sweep this column's gatherings for staged edits so the
  // cards can be flagged.
  //
  // Only the Published column is swept: staging only happens once a gathering is
  // live (api/logic/gathering.py routes the edit through the pending-edit
  // service only for PUBLISHED gatherings), so every other column would spend
  // a request per card to learn "none". The sweep also pauses while collapsed.
  const isPendingEditColumn = status === GatheringStatus.PUBLISHED;
  const [includePastGatherings, setIncludePastGatherings] = useState<boolean>(false);
  const {
    pendingEdits,
    pendingEditCount,
    isSweeping: isSweepingPendingEdits,
    error: pendingEditError,
    refresh: refreshPendingEdits
  } = usePendingEditSweep(sortedGatherings, token, {
    enabled: isPendingEditColumn && !collapsed,
    includePastGatherings
  });

  // Manual re-check: force a fresh read and widen it to past gatherings, which
  // the default sweep skips to keep the fanout small.
  const handleRecheckPendingEdits = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIncludePastGatherings(true);
    refreshPendingEdits();
  };

  // Get a human-readable status name
  const getStatusName = (status: GatheringStatus): string => {
    return status.replace(/_/g, ' ');
  };

  // Get appropriate color for the status
  const getStatusColor = (status: GatheringStatus): string => {
    switch (status) {
      case GatheringStatus.NEW:
        return '#3498db'; // Blue
      case GatheringStatus.IN_REVIEW:
        return '#f39c12'; // Orange
      case GatheringStatus.NEED_MORE_INFO:
        return '#e74c3c'; // Red
      case GatheringStatus.RESCHEDULE:
        return '#9b59b6'; // Purple
      case GatheringStatus.DENIED:
        return '#7f8c8d'; // Gray
      case GatheringStatus.AWAITING_GATHERING_LINK:
        return '#2ecc71'; // Green
      case GatheringStatus.READY_TO_PUBLISH:
        return '#1abc9c'; // Teal
      case GatheringStatus.PUBLISHED:
        return '#27ae60'; // Dark Green
      default:
        return '#95a5a6'; // Light Gray
    }
  };

  // Handle toggle click to prevent event bubbling
  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleCollapse) {
      onToggleCollapse();
    }
  };

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (!loadMore || isLoading || collapsed) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading) {
          loadMore();
        }
      },
      { threshold: 0.5 }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [loadMore, isLoading, collapsed]);

  if (collapsed) {
    return (
      <div 
        className="column column-minimized"
        style={{ backgroundColor: getStatusColor(status) }}
        ref={columnRef}
      >
        <div className="minimized-content">
          <div className="minimized-title">
            <span>{getStatusName(status)}</span>
          </div>
          <div className="minimized-count">
            {sortedGatherings.length}
          </div>
          {isPendingEditColumn && pendingEditCount > 0 && (
            <div
              className="minimized-count"
              title={`${pendingEditCount} gathering(s) with host changes awaiting review`}
              style={{ backgroundColor: '#fff4e0', color: '#8a5300' }}
            >
              ✎ {pendingEditCount}
            </div>
          )}
          <button 
            className="expand-button"
            onClick={handleToggleClick}
            aria-label="Expand column"
          >
            ⟩
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="column" ref={columnRef}>
      <div 
        className="column-header" 
        style={{ backgroundColor: getStatusColor(status) }}
      >
        <h3>{getStatusName(status)}</h3>
        <div className="column-header-controls">
          <span className="gathering-count">
            {sortedGatherings.length}
          </span>

          {/* Host changes awaiting review in this column (ENG-1097) */}
          {isPendingEditColumn && (
            <Tooltip
              title={
                isSweepingPendingEdits
                  ? 'Checking for host changes awaiting review…'
                  : pendingEditCount > 0
                    ? `${pendingEditCount} gathering(s) with host changes awaiting review. Click to re-check, including past gatherings.`
                    : 'No host changes awaiting review. Click to re-check, including past gatherings.'
              }
              arrow
            >
              <IconButton
                size="small"
                onClick={handleRecheckPendingEdits}
                disabled={isSweepingPendingEdits}
                sx={{
                  color: 'white',
                  padding: '4px',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                }}
                aria-label="Re-check for host changes awaiting review"
              >
                <EditNoteIcon fontSize="small" />
                {pendingEditCount > 0 && (
                  <span style={{ fontSize: '0.75rem', marginLeft: '2px', fontWeight: 700 }}>
                    {pendingEditCount}
                  </span>
                )}
              </IconButton>
            </Tooltip>
          )}

          {/* Sort dropdown button */}
          <Tooltip 
            title={sortBy === 'gathering_date' ? 'Sort: Gathering Date' : 'Sort: Submission Date'}
            arrow
          >
            <IconButton
              size="small"
              onClick={handleSortMenuClick}
              sx={{ 
                color: 'white',
                padding: '4px',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
              }}
              aria-label="Sort options"
            >
              <SortIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          {/* Sort dropdown menu */}
          <Menu
            anchorEl={sortMenuAnchor}
            open={sortMenuOpen}
            onClose={handleSortMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem 
              selected={sortBy === 'gathering_date'}
              onClick={() => handleSortSelect('gathering_date')}
            >
              Gathering Date
            </MenuItem>
            <MenuItem 
              selected={sortBy === 'submission_date'}
              onClick={() => handleSortSelect('submission_date')}
            >
              Submission Date
            </MenuItem>
          </Menu>
          
          {onToggleCollapse && (
            <button 
              className="column-toggle-button"
              onClick={handleToggleClick}
              aria-label="Collapse column"
            >
              ⟨
            </button>
          )}
        </div>
      </div>
      
      <div 
        className={`column-content ${isOver ? (canDrop ? 'drop-target can-drop' : 'drop-target cannot-drop') : ''}`}
        ref={contentRef}
      >
        {error && (
          <div className="column-error">
            <p>{error}</p>
            <button onClick={refresh}>Retry</button>
          </div>
        )}

        {/* A partially-failed sweep would otherwise silently understate the
            number of gatherings waiting on a decision. */}
        {isPendingEditColumn && pendingEditError && (
          <div className="column-error">
            <p>{pendingEditError}</p>
            <button onClick={refreshPendingEdits}>Retry</button>
          </div>
        )}

        {isLoading && sortedGatherings.length === 0 ? (
          <div className="column-loading">
            <div className="spinner"></div>
            <p>Loading gatherings...</p>
          </div>
        ) : sortedGatherings.length > 0 ? (
          <>
            {sortedGatherings.map(gathering => (
              <GatheringCard
                key={gathering.id}
                gathering={gathering}
                onUpdateStatus={onUpdateStatus}
                onViewDetails={onViewDetails ? () => onViewDetails(gathering.id) : undefined}
                pendingEdit={pendingEdits[gathering.id]}
              />
            ))}
            
            {isBackgroundLoading && (
              <div className="loading-more" ref={loadingRef}>
                <div className="spinner small"></div>
                <p>Loading more...</p>
              </div>
            )}
          </>
        ) : (
          <div className="empty-column">
            <p>No gatherings with status "{getStatusName(status)}"</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Column;